import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ArrayHelper } from '@kildencore/helpers/array.helper';
import { CatalogTreeService } from '@kildencore/services/data/catalog-tree.service';
import { SearchService } from '@kildencore/services/data/search.service';
import { KildenStateService } from '@kildencore/services/kilden-state.service';
import { liveSearch, moveCursorToStart } from '@kildencore/tools';
import { CatalogTreeItem } from '@kildenshared/components/catalog-tree/catalog-tree-item';
import { CatalogTreeRoot } from '@kildenshared/components/catalog-tree/catalog-tree-root';
import { CatalogTreeComponent } from '@kildenshared/components/catalog-tree/catalog-tree.component';
import { map, Observable } from 'rxjs';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';

@Component({
  providers: [CatalogTreeComponent],
  selector: 'kilden3-layer-search',
  templateUrl: './layer-search.component.html',
  styleUrls: ['./layer-search.component.css'],
  imports: [
    FormsModule,
    MatInput,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatAutocomplete,
    MatOption,
  ],
})
export class LayerSearchComponent implements OnInit {
  private ss = inject(SearchService);
  private kss = inject(KildenStateService);
  private ctc = inject(CatalogTreeComponent);
  private cts = inject(CatalogTreeService);

  public topic = '';
  currentTitle = '';
  searchFormControl = new FormControl('');
  filteredLayers: Observable<string[]> | undefined;
  stateGroupOptions: CatalogTreeItem[] | undefined;
  private catalogTree: CatalogTreeRoot | undefined;

  ngOnInit() {
    this.searchFormControl.valueChanges
      .pipe(
        liveSearch((value: string | null) => {
          this.getTopic();
          return this.ss.getMapLayers(value, this.topic);
        })
      )
      .pipe(
        map((data: CatalogTreeItem[]) => {
          this.stateGroupOptions = data;
        })
      )
      .subscribe();

    this.cts.catalogTree$.subscribe(t => {
      this.catalogTree = t;
    });
  }

  private getTopic() {
    this.kss.topic$.subscribe(d => (this.topic = d));
  }

  customStopPropagation(e: KeyboardEvent) {
    if (e.key === ' ') {
      e.stopPropagation();
    }
  }

  clearSearch() {
    this.searchFormControl.setValue('');
    this.currentTitle = '';
    moveCursorToStart('search-layer-input-field');
  }

  displayFn(option: CatalogTreeItem): string {
    return option && option.label && option.parentLabel ? `${option.label} - ${option.parentLabel}` : '';
  }

  onChange(selectedLayer: CatalogTreeItem) {
    this.currentTitle = `${selectedLayer.label} - ${selectedLayer.parentLabel}` || '';
    moveCursorToStart('search-layer-input-field');

    const catalogTree = this.catalogTree?.children;
    if (!catalogTree) return;

    // Use the catalog tree item so the search result and the layer list do not diverge.
    const treeItem: CatalogTreeItem = ArrayHelper.nestedFind(catalogTree, selectedLayer.id, 'id');
    if (typeof treeItem !== 'object' || Array.isArray(treeItem)) return;

    selectedLayer = treeItem;

    // Include the selected layer and its direct children in the activation update.
    const childrenIds = this._getNestedIdsFromLayer(selectedLayer);

    // Search adds layers without resetting existing active layers.
    for (const i of catalogTree) {
      this.ctc.markSelected(i, childrenIds, [catalogTree], undefined, true);
    }
  }

  private _getNestedIdsFromLayer(selectedLayer: CatalogTreeItem): string[] {
    const ids = selectedLayer.idBod?.length ? [selectedLayer.idBod] : [];
    if (selectedLayer.children?.length) {
      selectedLayer.children?.forEach((child: CatalogTreeItem) => {
        if (child.idBod?.length) {
          ids.push(child.idBod);
        }
      });
    }
    return ids;
  }
}
